/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('faq-header')) {
  const FaqHeader = class extends HTMLElement {
    connectedCallback() {
      this.classNames = {
        questionContainerInactive: 'faq-search-item-inactive',
        sectionWithIndexStatus: 'section-faq-header--with-index'
      };

      this.searchInput = this.querySelector('.faq-search__input');
      if (this.searchInput) {
        this.debouncedPerformSearch = debounce(this.performSearch.bind(this), 50);
        this.searchInput.addEventListener('input', this.debouncedPerformSearch);
      }

      this.debouncedResizeIndex = debounce(this.resizeIndex.bind(this), 250);
      this.hasStickyBounds = Boolean(
        this.querySelector('.faq-index__sticky-container') || this.querySelector('.faq-header__content-sticky-container')
      );
      if (this.hasStickyBounds) {
        window.addEventListener('resize', this.debouncedResizeIndex);
      }

      this.debouncedBuildIndex = debounce(this.buildIndex.bind(this), 50);
      document.addEventListener('theme:faq-header-update', this.debouncedBuildIndex);
      this.debouncedBuildIndex();

      if (this.querySelector('.faq-index')) {
        this.addEventListener('click', FaqHeader.handleIndexClick.bind(this));
        this.closest('.section-faq-header').classList.add(this.classNames.sectionWithIndexStatus);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('theme:faq-header-update', this.debouncedBuildIndex);
      if (this.searchInput && this.debouncedPerformSearch) {
        this.searchInput.removeEventListener('input', this.debouncedPerformSearch);
      }
      if (this.hasStickyBounds) {
        window.removeEventListener('resize', this.debouncedResizeIndex);
      }
    }

    buildIndex() {
      const faqHeaderSection = this.closest('.section-faq-header');
      const indexContainer = this.querySelector('.faq-index__item-container');

      if (indexContainer) {
        indexContainer.querySelectorAll('.faq-index-item').forEach((el) => {
          el.parentNode.removeChild(el);
        });
      }

      this.linkedCollapsibleTabs = [];
      this.linkedQuestionContainers = [];
      this.linkedContent = [];

      let currentElement = faqHeaderSection;
      while (currentElement.nextElementSibling && currentElement.nextElementSibling.classList.contains('section-collapsible-tabs')) {
        currentElement = currentElement.nextElementSibling;

        // build list of searchable content
        this.linkedCollapsibleTabs.push(currentElement);
        currentElement.querySelectorAll('.collapsible-tabs__tab').forEach((el) => this.linkedQuestionContainers.push(el));
        currentElement.querySelectorAll('.collapsible-tabs__content').forEach((el) => this.linkedContent.push(el));

        // build index UI
        if (indexContainer) {
          const currentElementHeading = currentElement.querySelector('.collapsible-tabs__heading');
          if (currentElementHeading) {
            const html = `
              <div class="faq-index-item">
                <a class="faq-index-item__link"></a>
              </div>`;
            const htmlFragment = document.createRange().createContextualFragment(html);
            const link = htmlFragment.querySelector('.faq-index-item__link');
            link.href = `#${currentElementHeading.id}`;
            link.innerHTML = currentElementHeading.innerHTML;
            indexContainer.appendChild(htmlFragment);
          }
        }
      }

      this.resizeIndex();
    }

    resizeIndex() {
      const stickyContainer = this.querySelector('.faq-index__sticky-container');
      const stickyContentContainer = this.querySelector('.faq-header__content-sticky-container');
      const stickyContentItem = this.querySelector('.faq-header__content-sticky-item');
      const stickyContentSpacer = this.querySelector('.faq-header__content-spacer');
      const isDesktop = window.matchMedia('(min-width: 768px)').matches;

      if (!stickyContainer && !stickyContentContainer) {
        return;
      }

      if (!isDesktop) {
        if (stickyContainer) stickyContainer.style.height = '';
        if (stickyContentContainer) stickyContentContainer.style.height = '';
        if (stickyContentSpacer) stickyContentSpacer.style.height = '';
        return;
      }

      const lastCollapsibleSection = this.linkedCollapsibleTabs?.[this.linkedCollapsibleTabs.length - 1];
      if (!lastCollapsibleSection) {
        if (stickyContainer) stickyContainer.style.height = '';
        if (stickyContentContainer) stickyContentContainer.style.height = '';
        if (stickyContentSpacer) stickyContentSpacer.style.height = '';
        return;
      }

      const lastSectionBottom = lastCollapsibleSection.getBoundingClientRect().bottom + window.pageYOffset;
      const setStickyContainerHeight = (container) => {
        if (!container) return;
        const containerTop = container.getBoundingClientRect().top + window.pageYOffset;
        container.style.height = `${Math.max(lastSectionBottom - containerTop, 0)}px`;
      };

      setStickyContainerHeight(stickyContainer);
      setStickyContainerHeight(stickyContentContainer);

      if (stickyContentSpacer && stickyContentItem) {
        stickyContentSpacer.style.height = `${stickyContentItem.offsetHeight}px`;
      }
    }

    performSearch() {
      if (!this.searchInput) return;
      if (!Array.isArray(this.linkedQuestionContainers) || !Array.isArray(this.linkedContent)) return;

      const terms = this.searchInput.value
        .trim()
        .split(/\s+/)
        .map((term) => term.toLowerCase())
        .filter(Boolean);

      // search
      this.linkedQuestionContainers.forEach((el) => {
        if (terms.length) {
          let termFound = false;
          const matchContent = el.textContent.toLowerCase();
          terms.forEach((term) => {
            if (matchContent.indexOf(term) >= 0) {
              termFound = true;
            }
          });
          if (termFound) {
            el.classList.remove(this.classNames.questionContainerInactive);
          } else {
            el.classList.add(this.classNames.questionContainerInactive);
          }
        } else {
          el.classList.remove(this.classNames.questionContainerInactive);
        }
      });

      // hide non-question content if doing a search
      this.linkedContent.forEach((el) => {
        if (terms.length) {
          el.classList.add(this.classNames.questionContainerInactive);
        } else {
          el.classList.remove(this.classNames.questionContainerInactive);
        }
      });

      // Recalculate sticky bounds after DOM visibility changes settle.
      requestAnimationFrame(() => this.resizeIndex());
    }

    static handleIndexClick(evt) {
      const link = evt.target.closest('.faq-index-item__link');
      if (link) {
        evt.preventDefault();
        const id = link.href.split('#')[1];
        const scrollTarget = document.getElementById(id);
        const stickyContentItem = this.querySelector('.faq-header__content-sticky-item');

        const calcScrollY = () => {
          let y = scrollTarget.getBoundingClientRect().top + window.pageYOffset;
          if (stickyContentItem) {
            const stickyTop = parseFloat(getComputedStyle(stickyContentItem).top) || 0;
            y -= stickyContentItem.offsetHeight + stickyTop;
          } else {
            const stickyHeight = getComputedStyle(document.documentElement).getPropertyValue('--theme-sticky-header-height');
            y -= stickyHeight ? parseInt(stickyHeight, 10) + 50 : 50;
          }
          return y;
        };

        // Custom RAF animation so we can recalculate the target on every frame.
        // If the layout shifts mid-scroll (announcement bar, lazy images, etc.)
        // the animation adapts in real-time instead of needing a second scroll.
        const startY = window.pageYOffset;
        const startTime = performance.now();
        const duration = 600;
        const ease = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

        const animate = (now) => {
          const progress = Math.min((now - startTime) / duration, 1);
          window.scrollTo(0, startY + (calcScrollY() - startY) * ease(progress));
          if (progress < 1) requestAnimationFrame(animate);
        };

        requestAnimationFrame(animate);
      }
    }
  };

  window.customElements.define('faq-header', FaqHeader);
}
